import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import { X } from 'lucide-react'
import { getSession } from '../../getSession'
import { addLeadFollowupDLTMassOperation, getFollowUpStatus, getUserFromHashedList, getProductsByName, getCollaboratorTypes, getTeamsByTypeId, getUserListByUserId } from '../../utils/lead'

const AddFollowupMassOperation = ({
  mode = 'followup',
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  onClose = () => { },
  registerSubmitHandler
}) => {
  const isAssignOnly = String(mode || '').toLowerCase() === 'assignto'
  const [assignTo, setAssignTo] = useState('')
  const [followupStatus, setFollowupStatus] = useState('')
  const [remarks, setRemarks] = useState('')
  const [nextStatusDate, setNextStatusDate] = useState(() => {
    const dt = new Date()
    dt.setDate(dt.getDate() + 1)
    return dt.toISOString().split('T')[0]
  })
  const [nextStatusTime, setNextStatusTime] = useState(() => {
    const dt = new Date()
    dt.setDate(dt.getDate() + 1)
    return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
  })
  const [conversionDate, setConversionDate] = useState(() => new Date().toISOString().split('T')[0])
  const [conversionTime, setConversionTime] = useState(() => {
    const dt = new Date()
    return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
  })
  const [amountPaid, setAmountPaid] = useState('')
  const [selectedProducts, setSelectedProducts] = useState([])
  const [productQuery, setProductQuery] = useState('')
  const [productSuggestions, setProductSuggestions] = useState([])
  const [isSearchingProducts, setIsSearchingProducts] = useState(false)
  const [showProductSuggestions, setShowProductSuggestions] = useState(false)
  const productDebounceRef = useRef(null)
  const [isReAssign, setIsReAssign] = useState(false)
  const [assignEnabled, setAssignEnabled] = useState(true)
  const [addCollaborator, setAddCollaborator] = useState(false)
  // collaborator state
  const [collabTypeOptions, setCollabTypeOptions] = useState([])
  const [collabTeamOptions, setCollabTeamOptions] = useState([])
  const [collabMode, setCollabMode] = useState('team')
  const [collabSelectedType, setCollabSelectedType] = useState({ id: '', name: '' })
  const [collabSelectedValue, setCollabSelectedValue] = useState({ id: '', name: '' })
  const [collaborators, setCollaborators] = useState([])
  const [collabUserOptions, setCollabUserOptions] = useState([])
  const [onSchedule, setOnSchedule] = useState(false)
  const [scheduleDateTime, setScheduleDateTime] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const [ownerOptions, setOwnerOptions] = useState([])
  const [statusOptions, setStatusOptions] = useState([])

  const normalizeUsers = (list) => {
    const source = Array.isArray(list) ? list : []
    return source
      .map((user) => {
        const value = String(user?.Code ?? user?.Id ?? user?.ID ?? user?.UserId ?? user?.Value ?? '').trim()
        const label = String(user?.Text ?? user?.TextN ?? user?.Name ?? user?.DisplayName ?? user?.LoginName ?? value).trim()
        return { value, label }
      })
      .filter((item) => item.value)
  }

  const normalizeStatuses = (list) => {
    const source = Array.isArray(list) ? list : []
    return source
      .map((status) => {
        const value = String(status?.Id ?? status?.Code ?? status?.Value ?? '').trim()
        const label = String(status?.Text ?? status?.FollowupStatus ?? status?.Name ?? value).trim()
        const rawCode = String(status?.RawCode ?? status?.Code ?? status?.code ?? status?.Value ?? status?.value ?? '').trim()
        const rawParts = rawCode.split(',').map((part) => String(part || '').trim())
        const statusFlag = Number.parseInt(rawParts[1] ?? status?.CanSetNextStatus ?? '', 10)
        const conversionFlag = Number.parseInt(rawParts[2] ?? status?.IsConverted ?? '', 10)
        const hasExplicitFlags = Number.isFinite(statusFlag) || Number.isFinite(conversionFlag)
        const normalizedLabel = label.toLowerCase()

        let fallbackStatusFlag = 1
        let fallbackConversionFlag = 0

        if (normalizedLabel.includes('not-interested') || normalizedLabel.includes('not interested') || normalizedLabel.includes('dead') || normalizedLabel.includes('wrong number')) {
          fallbackStatusFlag = 0
          fallbackConversionFlag = 0
        } else if (normalizedLabel.includes('converted')) {
          fallbackStatusFlag = 0
          fallbackConversionFlag = 1
        } else if (normalizedLabel.includes('call-back') || normalizedLabel.includes('callback')) {
          fallbackStatusFlag = 1
          fallbackConversionFlag = 0
        }

        return {
          value,
          label,
          rawCode,
          canSetNextStatus: hasExplicitFlags
            ? (Number.isFinite(statusFlag) ? statusFlag : 0)
            : fallbackStatusFlag,
          isConverted: hasExplicitFlags
            ? (Number.isFinite(conversionFlag) ? conversionFlag : 0)
            : fallbackConversionFlag
        }
      })
      .filter((item) => item.value || item.label)
  }

  useEffect(() => {
    let mounted = true

    const loadOptions = async () => {
      try {
        const { userId, TokenId, token, parentId } = getSession()

        const usersPayload = {
          Token: TokenId || token || '',
          Message: '',
          LoggedUserId: Number(userId) || 0,
          MAC_Address: '',
          IP_Address: '',
          Details: { UserId: Number(userId) || 0 },
          BroadcastName: ''
        }

        const statusPayload = {
          Token: TokenId || token || '',
          Message: '',
          LoggedUserId: Number(userId) || 0,
          MAC_Address: '',
          IP_Address: '',
          Details: { ParentId: Number(parentId) || 0 },
          BroadcastName: ''
        }

        const usersResp = await getUserFromHashedList(usersPayload)

        let statusResp = []
        if (!isAssignOnly) {
          statusResp = await getFollowUpStatus(statusPayload)
        }

        let usersRaw = usersResp?.Details ?? usersResp?.d ?? usersResp ?? []
        if (usersRaw && usersRaw.data) usersRaw = usersRaw.data
        if (usersRaw && !Array.isArray(usersRaw)) {
          usersRaw = usersRaw.ResultList ?? usersRaw.Users ?? usersRaw.UserList ?? usersRaw.Data ?? usersRaw.Items ?? usersRaw
        }

        const normalizedUsers = normalizeUsers(usersRaw)
        const normalizedStatuses = normalizeStatuses(statusResp)

        if (mounted) {
          setOwnerOptions(normalizedUsers)
          setStatusOptions(isAssignOnly ? [] : normalizedStatuses)
          if (!assignTo && normalizedUsers.length) {
            setAssignTo(normalizedUsers[0].value)
          }
          if (!isAssignOnly && !followupStatus && normalizedStatuses.length) {
            setFollowupStatus(normalizedStatuses[0].value)
          }
        }
      } catch (loadError) {
        console.warn('AddFollowupMassOperation load options error:', loadError?.message || loadError)
      }
    }

    loadOptions()
    return () => { mounted = false }
  }, [isAssignOnly])

  // load collaborator types & users when addCollaborator is enabled
  useEffect(() => {
    let alive = true
    if (!addCollaborator) return () => { alive = false }

    const { TokenId, userId } = getSession()
    getCollaboratorTypes({ Token: TokenId || '', Message: '', LoggedUserId: userId || 0, MAC_Address: '', IP_Address: '', Details: { UserID: userId || 0, ClNmae: '' }, BroadcastName: '' })
      .then(list => {
        if (!alive) return
        setCollabTypeOptions(Array.isArray(list) ? list.map(t => ({ id: String(t?.Id ?? t?.id ?? t?.Code ?? t?.code ?? ''), name: t?.TypeName ?? t?.type_name ?? t?.typeName ?? t?.Text ?? t?.text ?? t?.Name ?? t?.name ?? '' })).filter(t => t.id || t.name) : [])
      }).catch(err => console.warn('collabType fetch error', err))

    getUserListByUserId({ Token: TokenId || '', Message: '', LoggedUserId: userId || 0, MAC_Address: '', IP_Address: '', Details: { UserId: userId || 0 }, BroadcastName: '' })
      .then(list => {
        if (!alive) return
        setCollabUserOptions(Array.isArray(list) ? list.map(u => {
          const id = String(u?.Code ?? u?.Id ?? u?.id ?? u?.UserId ?? u?.code ?? u?.Value ?? '').trim()
          const name = String(u?.Text ?? u?.TextN ?? u?.Name ?? u?.UserName ?? u?.LoginName ?? id).trim()
          return { id, name }
        }).filter(u => u.id || u.name) : [])
      }).catch(err => console.warn('collabUsers fetch error', err))

    return () => { alive = false }
  }, [addCollaborator])

  // load teams when collab type changes and mode = team
  useEffect(() => {
    let alive = true
    if (!addCollaborator || collabMode !== 'team' || !collabSelectedType.id) return () => { alive = false }
    const { TokenId, userId } = getSession()
    getTeamsByTypeId({ Token: TokenId || '', Message: '', LoggedUserId: userId || 0, MAC_Address: '', IP_Address: '', Details: { UserID: userId || 0, CollTeamName: '' }, BroadcastName: '' })
      .then(list => {
        if (!alive) return
        setCollabTeamOptions(Array.isArray(list) ? list.map(t => {
          const id = String(t?.Id ?? t?.id ?? t?.Code ?? t?.code ?? '').trim()
          const name = String(t?.TeamName ?? t?.team_name ?? t?.teamName ?? t?.Text ?? t?.text ?? t?.Name ?? t?.name ?? id).trim()
          return { id, name }
        }).filter(t => t.id || t.name) : [])
      }).catch(err => console.warn('collabTeam fetch error', err))

    return () => { alive = false }
  }, [addCollaborator, collabMode, collabSelectedType.id])

  // Debounced product search for mass operation
  const fetchProductSuggestions = async (q) => {
    if (!q || String(q).trim().length === 0) {
      setProductSuggestions([])
      setShowProductSuggestions(false)
      return []
    }

    setIsSearchingProducts(true)
    try {
      const { userId, TokenId, token } = getSession()
      const payload = {
        Token: TokenId || token || '',
        Message: '',
        LoggedUserId: Number(userId) || 0,
        MAC_Address: '',
        IP_Address: '',
        Details: { stName: q, stUserID: userId },
        BroadcastName: ''
      }
      const resp = await getProductsByName(payload)
      const arr = Array.isArray(resp) ? resp : (resp?.Details && Array.isArray(resp.Details) ? resp.Details : [])
      const mapped = arr.map(i => ({ id: i?.Id ?? i?.ProductID ?? i?.ProductId ?? i?.Value ?? '', text: i?.Text ?? i?.Name ?? i?.ProductName ?? String(i?.Id ?? '') }))
      return mapped
    } catch (err) {
      console.warn('mass getProductsByName error', err)
      return []
    } finally {
      setIsSearchingProducts(false)
    }
  }

  useEffect(() => {
    if (productDebounceRef.current) clearTimeout(productDebounceRef.current)
    if (!productQuery || String(productQuery).trim().length === 0) {
      setProductSuggestions([])
      setShowProductSuggestions(false)
      return
    }

    const qSnapshot = productQuery
    let cancelled = false
    productDebounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchProductSuggestions(qSnapshot)
        if (cancelled) return
        if (qSnapshot !== productQuery) return
        const has = Array.isArray(results) && results.length > 0
        setProductSuggestions(results || [])
        setShowProductSuggestions(!!has)
      } catch (e) { console.warn(e) }
    }, 350)

    return () => { cancelled = true; if (productDebounceRef.current) clearTimeout(productDebounceRef.current) }
  }, [productQuery])

  useEffect(() => {
    if (!onSchedule) setScheduleDateTime('')
  }, [onSchedule])

  const formatDateTime = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) return ''

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const dateParts = String(dateValue).split('-')
    if (dateParts.length !== 3) return ''

    const [yyyy, mm, dd] = dateParts
    const monthName = months[Math.max(0, Number(mm) - 1)] || ''
    if (!monthName) return ''

    const safeTime = String(timeValue).trim().slice(0, 5)
    if (!safeTime) return ''

    return `${String(dd).padStart(2, '0')}-${monthName}-${yyyy} ${safeTime}:00`
  }

  const parseAmount = (val) => {
    const raw = String(val ?? '').replace(/,/g, '').replace(/[^0-9.\-]/g, '').trim()
    if (raw === '') return 0
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }

  const selectedStatusConfig = useMemo(() => {
    const selected = statusOptions.find((option) => String(option.value) === String(followupStatus))
    if (!selected) {
      return {
        showAssign: false,
        showDateTime: false,
        showProducts: false,
        showAmount: false,
        showRemarks: false,
        dateLabel: 'Next Status Date',
        timeLabel: 'Next Status Time',
        useConversionDate: false
      }
    }

    const statusFlag = Number(selected.canSetNextStatus) || 0
    const conversionFlag = Number(selected.isConverted) || 0

    if (statusFlag === 0 && conversionFlag === 0) {
      return {
        showAssign: false,
        showDateTime: false,
        showProducts: false,
        showAmount: false,
        showRemarks: true,
        dateLabel: 'Next Status Date',
        timeLabel: 'Next Status Time',
        useConversionDate: false
      }
    }

    if (statusFlag === 1 && conversionFlag === 0) {
      return {
        showAssign: true,
        showDateTime: true,
        showProducts: true,
        showAmount: false,
        showRemarks: true,
        dateLabel: 'Next Status Date',
        timeLabel: 'Next Status Time',
        useConversionDate: false
      }
    }

    if (statusFlag === 0 && conversionFlag === 1) {
      return {
        showAssign: true,
        showDateTime: true,
        showProducts: true,
        showAmount: true,
        showRemarks: true,
        dateLabel: 'Conversion Date',
        timeLabel: 'Conversion Time',
        useConversionDate: true
      }
    }

    return {
      showAssign: true,
      showDateTime: true,
      showProducts: true,
      showAmount: true,
      showRemarks: true,
      dateLabel: 'Conversion Date',
      timeLabel: 'Conversion Time',
      useConversionDate: false
    }
  }, [statusOptions, followupStatus])

  const validate = useCallback(() => {
    setError('')

    if ((isAssignOnly || selectedStatusConfig.showAssign) && !assignTo) {
      setError('Please select Assign To user.')
      return false
    }

    if (!isAssignOnly && !followupStatus) {
      setError('Please select Followup Status.')
      return false
    }

    if (!isAssignOnly && selectedStatusConfig.showDateTime) {
      const activeDate = selectedStatusConfig.useConversionDate ? conversionDate : nextStatusDate
      const activeTime = selectedStatusConfig.useConversionDate ? conversionTime : nextStatusTime
      if (!activeDate || !activeTime) {
        setError(`Please select ${selectedStatusConfig.dateLabel} and ${selectedStatusConfig.timeLabel}.`)
        return false
      }
    }

    if (!isAssignOnly && selectedStatusConfig.showRemarks && !String(remarks || '').trim()) {
      setError('Remarks is required.')
      return false
    }

    if (addCollaborator && collaborators.length === 0) {
      setError('Please add at least one collaborator.')
      return false
    }

    return true
  }, [
    assignTo,
    followupStatus,
    isAssignOnly,
    nextStatusDate,
    nextStatusTime,
    conversionDate,
    conversionTime,
    selectedStatusConfig,
    remarks
  ])

  const handleSave = useCallback(async () => {
    if (!validate()) return

    const { userId, parentId, TokenId, token } = getSession()
    const numericUserId = Number(userId) || 0
    const numericParentId = Number(parentId) || 0
    const numericBatchId = Number(massOpBatchId) || 0
    const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.map((id) => String(id)).filter(Boolean).join('/') : '') || '').trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
    const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

    if (!idsForPayload && !shouldUseBatchId) {
      setError('No leads selected.')
      return
    }

    // prepare collaborator ids for payload
    const typeIds = []
    const teamIds = []
    const userIds = []
    if (addCollaborator && Array.isArray(collaborators) && collaborators.length) {
      collaborators.forEach((c) => {
        typeIds.push(String(c.typeId || 0))
        if (String(c.mode).toLowerCase() === 'team') {
          teamIds.push(String(c.selectedId || 0))
          userIds.push('0')
        } else {
          teamIds.push('0')
          userIds.push(String(c.selectedId || 0))
        }
      })
    }

    const payload = {
      Token: TokenId || token || '',
      Message: '',
      LoggedUserId: numericUserId,
      MAC_Address: '',
      IP_Address: '',
      Details: {
        objLeadFollowup_BO: {
          LeadId: (Array.isArray(selectedLeadIds) && selectedLeadIds.length === 1)
            ? Number(selectedLeadIds[0])
            : 0,
          AssignedTo: (isAssignOnly || selectedStatusConfig.showAssign) ? (Number(assignTo) || numericUserId) : 0,
          FollowUpStatus: isAssignOnly ? 0 : (Number(followupStatus) || 0),
          NextStatusDate: isAssignOnly
            ? ''
            : selectedStatusConfig.showDateTime
              ? formatDateTime(
                selectedStatusConfig.useConversionDate ? conversionDate : nextStatusDate,
                selectedStatusConfig.useConversionDate ? conversionTime : nextStatusTime
              )
              : '',
          Products: isAssignOnly ? '' : (selectedStatusConfig.showProducts ? selectedProducts.map(p => p.text).join(',') : ''),
          AmountPaid: isAssignOnly ? 0 : (selectedStatusConfig.showAmount ? parseAmount(amountPaid) : 0),
          Remarks: remarks || '',
          IsReAssign: isAssignOnly ? true : !!isReAssign,
          NotifyBySMS: false,
          NotifyByEmail: false,
          SmsScheduelDateTime: onSchedule && scheduleDateTime ? formatDateTime(scheduleDateTime.split('T')[0], scheduleDateTime.split('T')[1]?.slice(0, 5)) : '',
          EmailScheduelDateTime: onSchedule && scheduleDateTime ? formatDateTime(scheduleDateTime.split('T')[0], scheduleDateTime.split('T')[1]?.slice(0, 5)) : '',
          LeadIds: idsForPayload,
          Ids: idsForPayload,
          Batchid: shouldUseBatchId ? numericBatchId : 0,
          TypeIds: addCollaborator ? typeIds.join(',') : '',
          TeamIds: addCollaborator ? teamIds.join(',') : '',
          UserIds: addCollaborator ? userIds.join(',') : ''
        },
        IDS: idsForPayload,
        HashedIds: idsForPayload,
        massbatchid: shouldUseBatchId ? numericBatchId : 0,
        strDLTTemplateID: '',
        strUserID: String(numericUserId),
        strTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        strParentID: String(numericParentId)
      },
      BroadcastName: ''
    }

    try {
      setIsSubmitting(true)
      const response = await addLeadFollowupDLTMassOperation(payload)
      const ok = response && (
        response.Status === 1 ||
        response.status === 1 ||
        (typeof response?.Message === 'string' && response.Message.toLowerCase().includes('success')) ||
        (typeof response?.Details === 'string' && response.Details.toLowerCase().includes('success'))
      )

      if (!ok) {
        throw new Error(response?.Message || response?.Details || 'Failed to save mass followup.')
      }

      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: isAssignOnly ? 'Mass AssignTo saved successfully.' : 'Mass Add Followup saved successfully.'
      })
      onClose()
    } catch (saveError) {
      console.error('AddFollowupMassOperation save error:', saveError)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: saveError?.message || 'Failed to save mass followup.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    validate,
    massOpBatchId,
    hashedIds,
    selectedLeadIds,
    applyOnAllLeads,
    assignTo,
    isAssignOnly,
    followupStatus,
    nextStatusDate,
    nextStatusTime,
    conversionDate,
    conversionTime,
    amountPaid,
    selectedProducts,
    isReAssign,
    onSchedule,
    scheduleDateTime,
    selectedStatusConfig,
    remarks,
    onClose
  ])

  const handleSaveRef = useRef(handleSave)
  useEffect(() => { handleSaveRef.current = handleSave }, [handleSave])

  // Unregister parent footer submit handler so we render inner Save/Cancel buttons instead
  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      try {
        registerSubmitHandler(null)
      } catch (e) {
        console.warn('[AddFollowupMassOperation] unregister submit handler failed', e)
      }
    }
    // no cleanup required
  }, [registerSubmitHandler])

  const selectedInfo = useMemo(() => {
    if (applyOnAllLeads) {
      return `Apply on all selected leads${massOpBatchId ? ` (Batch ID: ${massOpBatchId})` : ''}`
    }
    return `${Number(selectedCount) || 0} selected lead(s)`
  }, [applyOnAllLeads, selectedCount, massOpBatchId])

  return (
    <div className="space-y-4">
      {/* Hide selected-info banner when a followup status is chosen */}
      {(!followupStatus) && (
        <div className="text-sm text-gray-600">{selectedInfo}</div>
      )}

      <div className="border border-gray-200 rounded">
        <div className="bg-gray-100 text-gray-800 font-semibold text-center py-2 border-b border-gray-200">Add Follow up</div>

        {!isAssignOnly && (
          <div className="p-4 border-b border-gray-200">
            <label className="block text-sm font-semibold text-gray-800 mb-2">Select Follow up</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={followupStatus}
              onChange={(event) => setFollowupStatus(event.target.value)}
              disabled={isSubmitting}
            >
              <option value="">Select FollowupStatus</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        )}

        {(isAssignOnly || selectedStatusConfig.showAssign) && (
          <div className="p-4 border-b border-gray-200">
            {!isAssignOnly && (
              <>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Select Assignment Options:</label>
                <div className="flex items-center gap-4 mb-3">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={assignEnabled}
                      onChange={(event) => setAssignEnabled(event.target.checked)}
                      disabled={isSubmitting}
                    />
                    Assign To
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={addCollaborator}
                      onChange={(event) => setAddCollaborator(event.target.checked)}
                      disabled={isSubmitting}
                    />
                    Add Collaborator
                  </label>
                </div>
              </>
            )}

            {assignEnabled && (
              <>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Assign To</label>
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={assignTo}
                  onChange={(event) => setAssignTo(event.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">Select user</option>
                  {ownerOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </>
            )}
            {addCollaborator && (
              <div className="p-4 border-t border-gray-100 bg-gray-50 rounded mt-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold">Add Collaborator</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                    <select
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                      value={collabSelectedType.id}
                      onChange={(e) => {
                        const id = e.target.value
                        const found = collabTypeOptions.find(t => String(t.id) === String(id))
                        setCollabSelectedType(found ? { id: String(found.id), name: found.name } : { id: '', name: '' })
                      }}
                    >
                      <option value="">-- Select Type --</option>
                      {collabTypeOptions.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Mode</label>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                        <input type="radio" name="collabMode" checked={collabMode === 'team'} onChange={() => setCollabMode('team')} />
                        Select Team
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                        <input type="radio" name="collabMode" checked={collabMode === 'user'} onChange={() => setCollabMode('user')} />
                        Select User
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
                    {collabMode === 'team' ? (
                      <select
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                        value={collabSelectedValue.id}
                        onChange={(e) => {
                          const val = String(e.target.value).trim()
                          const found = collabTeamOptions.find(x => String(x.id).trim() === val)
                          setCollabSelectedValue(found ? { id: found.id, name: found.name } : { id: '', name: '' })
                        }}
                      >
                        <option value="">-- Select Team --</option>
                        {collabTeamOptions.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                      </select>
                    ) : (
                      <select
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                        value={collabSelectedValue.id}
                        onChange={(e) => {
                          const val = String(e.target.value).trim()
                          const found = collabUserOptions.find(x => String(x.id).trim() === val)
                          setCollabSelectedValue(found ? { id: found.id, name: found.name } : { id: '', name: '' })
                        }}
                      >
                        <option value="">-- Select User --</option>
                        {collabUserOptions.map(u => (<option key={u.id} value={u.id}>{u.name}</option>))}
                      </select>
                    )}
                  </div>
                </div>

                <div className="mt-3">
                  <button type="button" className="px-3 py-1.5 bg-green-100 text-green-700 rounded" onClick={() => {
                    if (!collabSelectedType.id || !collabSelectedValue.id) return
                    const exists = collaborators.some(c => c.typeId === collabSelectedType.id && c.mode === collabMode && c.selectedId === collabSelectedValue.id)
                    if (exists) return
                    setCollaborators(prev => [...prev, { typeId: collabSelectedType.id, typeName: collabSelectedType.name, mode: collabMode, selectedId: collabSelectedValue.id, selectedName: collabSelectedValue.name }])
                    setCollabSelectedValue({ id: '', name: '' })
                  }}>+ Add</button>
                </div>

                {collaborators.length > 0 && (
                  <div className="mt-4 bg-white border border-gray-100 rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs text-gray-500">
                        <tr>
                          <th className="px-3 py-2">TYPE</th>
                          <th className="px-3 py-2">MODE</th>
                          <th className="px-3 py-2">SELECTED</th>
                          <th className="px-3 py-2">ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {collaborators.map((c, i) => (
                          <tr key={`${c.typeId}-${c.mode}-${c.selectedId}-${i}`} className="border-t">
                            <td className="px-3 py-2">{c.typeName}</td>
                            <td className="px-3 py-2">{c.mode === 'team' ? 'Team' : 'User'}</td>
                            <td className="px-3 py-2">{c.selectedName}</td>
                            <td className="px-3 py-2"><button type="button" className="px-2 py-1 bg-red-500 text-white rounded" onClick={() => setCollaborators(prev => prev.filter((_, idx) => idx !== i))}>Remove</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!isAssignOnly && selectedStatusConfig.showDateTime && (
          <div className="p-4 border-b border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{selectedStatusConfig.dateLabel}</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={selectedStatusConfig.useConversionDate ? conversionDate : nextStatusDate}
                onChange={(event) => {
                  if (selectedStatusConfig.useConversionDate) {
                    setConversionDate(event.target.value)
                  } else {
                    setNextStatusDate(event.target.value)
                  }
                }}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{selectedStatusConfig.timeLabel}</label>
              <input
                type="time"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={selectedStatusConfig.useConversionDate ? conversionTime : nextStatusTime}
                onChange={(event) => {
                  if (selectedStatusConfig.useConversionDate) {
                    setConversionTime(event.target.value)
                  } else {
                    setNextStatusTime(event.target.value)
                  }
                }}
                disabled={isSubmitting}
              />
            </div>
          </div>
        )}

        {!isAssignOnly && selectedStatusConfig.showAmount && (
          <div className="p-4 border-b border-gray-200">
            <label className="block text-sm font-semibold text-gray-800 mb-1">Amount paid</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Amount Paid"
              value={amountPaid}
              onChange={(event) => setAmountPaid(event.target.value)}
              disabled={isSubmitting}
            />
          </div>
        )}

        {!isAssignOnly && selectedStatusConfig.showRemarks && (
          <div className="p-4 border-b border-gray-200">
            <label className="block text-sm font-semibold text-gray-800 mb-1">Remarks <span className="text-red-500">*</span></label>
            <textarea
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Remarks"
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              disabled={isSubmitting}
            />
          </div>
        )}

        {!isAssignOnly && selectedStatusConfig.showProducts && (
          <div className="p-4 border-b border-gray-200">
            <label className="block text-sm font-semibold text-gray-800 mb-1">Products</label>
            <div className="relative">
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedProducts.map((p, idx) => (
                  <span key={`${p.id}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    {p.text}
                    <button
                      type="button"
                      onClick={() => setSelectedProducts(prev => prev.filter((_, i) => i !== idx))}
                      className="hover:text-blue-900"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={selectedProducts.length > 0 ? "Add more products..." : "Enter product"}
                value={productQuery}
                onChange={(event) => setProductQuery(event.target.value)}
                onFocus={() => setShowProductSuggestions(true)}
                disabled={isSubmitting}
              />
              {showProductSuggestions && productSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded shadow max-h-40 overflow-auto">
                  {productSuggestions.map((p) => (
                    <div
                      key={p.id || p.text}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        if (!selectedProducts.some(existing => existing.id === p.id)) {
                          setSelectedProducts(prev => [...prev, p])
                        }
                        setProductQuery('')
                        setShowProductSuggestions(false)
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    >
                      {p.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!isAssignOnly && selectedStatusConfig.showAssign && (
          <div className="p-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={isReAssign}
                onChange={(event) => setIsReAssign(event.target.checked)}
                disabled={isSubmitting}
              />
              Re-Assign
            </label>
          </div>
        )}
      </div>

      {!isAssignOnly && (
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={onSchedule}
              onChange={(event) => setOnSchedule(event.target.checked)}
              disabled={isSubmitting}
            />
            On Schedule Date And Time
          </label>

          {onSchedule && (
            <div>
              <label className="block text-sm font-semibold text-gray-800">Save on Schedule Date and Time</label>
              <input
                type="datetime-local"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={scheduleDateTime}
                onChange={(event) => setScheduleDateTime(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>
      )}

      {isAssignOnly && (
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">Remarks</label>
          <textarea
            rows={3}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Optional remarks for assign operation"
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            disabled={isSubmitting}
          />
        </div>
      )}

      {/* Inner action buttons: Cancel + Save (use local handlers) */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="px-4 py-2 bg-white text-gray-800 border border-gray-300 rounded hover:bg-gray-50 transition disabled:opacity-60"
          onClick={() => {
            try { onClose() } catch (e) { console.error('Cancel handler error', e) }
          }}
          disabled={isSubmitting}
        >
          Cancel
        </button>

        <button
          type="button"
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-60"
          onClick={() => {
            console.debug('[AddFollowupMassOperation] Save button clicked')
            try {
              if (handleSaveRef.current) handleSaveRef.current()
            } catch (e) { console.error('Save button handler error', e) }
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  )
}

export default AddFollowupMassOperation
